import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  CommunicationDeliveryMode,
  CommunicationPurpose,
  CommunicationSuppressionReason,
  CustomerCommunicationStatus,
  REPORT_MAX_RANGE_DAYS,
  REPORT_PRESETS,
  ReportArea,
  ReportPreset,
  type CrmReportQuery,
} from '@optiflow/types';
import { reportAreaVisible } from '@optiflow/shared';
import { Alert } from '@/components/ui/Alert';
import { Card } from '@/components/ui/Card';
import { PageHeader } from '@/components/ui/PageHeader';
import { useLocale } from '@/hooks/useLocale';
import { apiErrorCode, apiErrorDetails } from '@/services/apiClient';
import { useAuthStore } from '@/store/authStore';
import { useCrmReportQuery } from '../api/reports.api';
import { CrmContent } from '../components/CrmContent';
import { failureReasonLabel } from '../components/crmLabels';
import { ReportFilterBar, type ReportFilters } from '../components/ReportFilterBar';
import { ReportMetaLine } from '../components/ReportMetaLine';
import { ReportTabs } from '../components/ReportTabs';

const PURPOSES = Object.values(CommunicationPurpose);
const STATUSES = Object.values(CustomerCommunicationStatus);
const MODES = Object.values(CommunicationDeliveryMode);
const SUPPRESSIONS = Object.values(CommunicationSuppressionReason);
const PERIOD_FIELDS = new Set(['preset', 'from', 'to']);
const FAILURE_CODE = /^[A-Z][A-Z0-9_]{0,63}$/;

const pick = <T extends string>(value: string | null, allowed: readonly T[]): T | '' =>
  allowed.includes(value as T) ? (value as T) : '';

/** The filters this page keeps in the URL: codes and one template id, never a name, a number or a message. */
function readFilters(params: URLSearchParams) {
  const preset = params.get('preset');
  const failure = params.get('failure') ?? '';
  return {
    period: {
      preset: (REPORT_PRESETS.includes(preset as ReportPreset)
        ? preset
        : ReportPreset.THIS_MONTH) as ReportPreset,
      from: params.get('from') ?? '',
      to: params.get('to') ?? '',
      branchId: params.get('branch') ?? '',
      compare: params.get('compare') === 'previous' ? ('previous' as const) : ('none' as const),
    } satisfies ReportFilters,
    purpose: pick(params.get('purpose'), PURPOSES),
    status: pick(params.get('status'), STATUSES),
    deliveryMode: pick(params.get('mode'), MODES),
    templateId: params.get('template') ?? '',
    suppressionReason: pick(params.get('suppression'), SUPPRESSIONS),
    failureCode: FAILURE_CODE.test(failure) ? failure : '',
  };
}

/**
 * Reports › CRM.
 *
 * Communication history, WhatsApp as of now and the automation rules — each authorised on the
 * server on its own. Nothing here says a message was delivered or read: "Sent to WhatsApp" is the
 * furthest the platform can see. Filters are codes in the URL, validated and refused by the server.
 */
export function CrmReportPage() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [params, setParams] = useSearchParams();
  const filters = readFilters(params);
  const { period } = filters;
  const grants = useAuthStore((state) => state.user?.permissions);
  const allowed = reportAreaVisible(grants, ReportArea.CRM);
  const customReady =
    period.preset !== ReportPreset.CUSTOM || (period.from !== '' && period.to !== '');

  const query: CrmReportQuery = {
    preset: period.preset,
    ...(period.preset === ReportPreset.CUSTOM ? { from: period.from, to: period.to } : {}),
    compare: period.compare,
    branchId: period.branchId || undefined,
    purpose: filters.purpose || undefined,
    status: filters.status || undefined,
    deliveryMode: filters.deliveryMode || undefined,
    templateId: filters.templateId || undefined,
    suppressionReason: filters.suppressionReason || undefined,
    failureCode: filters.failureCode || undefined,
  };
  const report = useCrmReportQuery(query, { enabled: customReady && allowed });
  const data = report.data;

  const write = (next: Partial<ReturnType<typeof readFilters>>) => {
    const merged = { ...filters, ...next, period: { ...period, ...(next.period ?? {}) } };
    const search = new URLSearchParams();
    search.set('preset', merged.period.preset);
    if (merged.period.preset === ReportPreset.CUSTOM) {
      if (merged.period.from) search.set('from', merged.period.from);
      if (merged.period.to) search.set('to', merged.period.to);
    }
    if (merged.period.compare === 'previous') search.set('compare', 'previous');
    if (merged.period.branchId) search.set('branch', merged.period.branchId);
    for (const [key, value] of [
      ['purpose', merged.purpose],
      ['status', merged.status],
      ['mode', merged.deliveryMode],
      ['template', merged.templateId],
      ['suppression', merged.suppressionReason],
      ['failure', merged.failureCode],
    ] as const) {
      if (value) search.set(key, value);
    }
    setParams(search, { replace: true });
  };

  const field = 'border-line bg-surface text-ink w-full rounded-lg border px-3 py-2 text-sm';
  const templates = (data?.volume?.byTemplate ?? []).filter((row) => row.id !== null);
  const failureCodes = [
    ...new Set([
      ...(data?.failures?.byReason ?? []).map((row) => row.code),
      ...(filters.failureCode ? [filters.failureCode] : []),
    ]),
  ];
  const anyFilter =
    filters.purpose ||
    filters.status ||
    filters.deliveryMode ||
    filters.templateId ||
    filters.suppressionReason ||
    filters.failureCode;

  // A refused filter and a refused period are different mistakes, each named in the reader's own language.
  const errorText = () => {
    if (apiErrorCode(report.error) !== 'VALIDATION_FAILED') return t('reports.loadError');
    const fields = apiErrorDetails(report.error).map((detail) => detail.field ?? '');
    return fields.some((name) => PERIOD_FIELDS.has(name))
      ? t('reports.invalidPeriod', { days: REPORT_MAX_RANGE_DAYS })
      : t('reports.crm.invalidFilter');
  };

  const select = (
    id: string,
    label: string,
    value: string,
    all: string,
    options: { value: string; label: string }[],
    onChange: (value: string) => void,
  ) => (
    <div>
      <label htmlFor={id} className="text-muted mb-1 block text-xs font-medium">
        {label}
      </label>
      <select
        id={id}
        className={field}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">{all}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <div className="space-y-5">
      <PageHeader title={t('reports.crm.title')} subtitle={t('reports.crm.subtitle')} />
      <ReportTabs />
      {allowed ? null : <Alert tone="info">{t('reports.crm.noAccess')}</Alert>}
      {allowed ? (
        <>
          <ReportFilterBar value={period} onChange={(next) => write({ period: next })} />
          <Card className="p-4">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {select(
                'crm-purpose',
                t('reports.crm.filters.purpose'),
                filters.purpose,
                t('reports.crm.filters.allPurposes'),
                PURPOSES.map((value) => ({ value, label: t(`crm.purpose.${value}`) })),
                (value) => write({ purpose: value as CommunicationPurpose | '' }),
              )}
              {select(
                'crm-status',
                t('reports.crm.filters.status'),
                filters.status,
                t('reports.crm.filters.allStatuses'),
                STATUSES.map((value) => ({ value, label: t(`crm.status.${value}`) })),
                (value) => write({ status: value as CustomerCommunicationStatus | '' }),
              )}
              {select(
                'crm-mode',
                t('reports.crm.filters.mode'),
                filters.deliveryMode,
                t('reports.crm.filters.allModes'),
                MODES.map((value) => ({ value, label: t(`reports.crm.mode.${value}`) })),
                (value) => write({ deliveryMode: value as CommunicationDeliveryMode | '' }),
              )}
              {data?.volume || filters.templateId
                ? select(
                    'crm-template',
                    t('reports.crm.filters.template'),
                    filters.templateId,
                    t('reports.crm.filters.allTemplates'),
                    [
                      ...templates.map((row) => ({
                        value: row.id as string,
                        label: (locale === 'ar' && row.labelAr) || row.label || '—',
                      })),
                      ...(filters.templateId &&
                      !templates.some((row) => row.id === filters.templateId)
                        ? [
                            {
                              value: filters.templateId,
                              label: data?.filters.templateName ?? filters.templateId,
                            },
                          ]
                        : []),
                    ],
                    (value) => write({ templateId: value }),
                  )
                : null}
              {select(
                'crm-suppression',
                t('reports.crm.filters.suppressionReason'),
                filters.suppressionReason,
                t('reports.crm.filters.allSuppressionReasons'),
                SUPPRESSIONS.map((value) => ({
                  value,
                  label: t(`reports.crm.suppressionReason.${value}`),
                })),
                (value) =>
                  write({ suppressionReason: value as CommunicationSuppressionReason | '' }),
              )}
              {select(
                'crm-failure',
                t('reports.crm.filters.failureReason'),
                filters.failureCode,
                t('reports.crm.filters.allFailureReasons'),
                failureCodes.map((value) => ({ value, label: failureReasonLabel(t, value) })),
                (value) => write({ failureCode: value }),
              )}
            </div>
            {data?.filters.templateName ? (
              <p className="text-muted mt-3 text-sm break-words">
                {t('reports.crm.filters.applied', { names: data.filters.templateName })}
              </p>
            ) : null}
            {anyFilter ? (
              <button
                type="button"
                className="text-brand-700 mt-3 text-sm font-medium hover:underline"
                onClick={() =>
                  write({
                    purpose: '',
                    status: '',
                    deliveryMode: '',
                    templateId: '',
                    suppressionReason: '',
                    failureCode: '',
                  })
                }
              >
                {t('reports.crm.filters.clear')}
              </button>
            ) : null}
          </Card>
        </>
      ) : null}
      {data ? <ReportMetaLine meta={data.meta} /> : null}
      {!customReady && allowed ? (
        <Alert tone="info">{t('reports.filters.chooseDates')}</Alert>
      ) : null}
      {report.isError ? <Alert tone="error">{errorText()}</Alert> : null}
      {report.isPending && customReady && allowed ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" aria-hidden>
          {Array.from({ length: 8 }).map((_, index) => (
            <div
              key={index}
              className="bg-surface border-line h-28 animate-pulse rounded-xl border"
            />
          ))}
        </div>
      ) : null}
      {data ? <CrmContent report={data} query={query} /> : null}
    </div>
  );
}
